import { addDynamicMoney, QuoteItemPriceAdjustment } from './quote-item-price-adjust-view';
import { applyV6PricesToQuoteItemPrice, isEmptyObject } from '../data/v6/helper-functions';
import { customElement } from 'lit/decorators.js';
import { DynamicValueBinder, EventValue, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { html, nothing } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangQuoteItemPriceAdjustSupplierView } from '../../../localization/scripts/quotes/views/quote-item-price-adjust-supplier-view-lang';
import { money } from '../../../webmodule-common/other/currency-formatter';
import { pricingStrategies } from '../../../webmodule-common/pricing/pricing-strategies';
import { QuoteContainerManager, StockLookupViewExtra } from '../data/quote-container';
import { QuoteFrameItemPrice } from '../../../webmodule-common/interop/webmodule-interop';
import { QuoteItemPrice, QuotePriceCalculation } from '../../api/dealer-api-interface-quote';

@customElement('wm-quoteitempriceadjustmentsupplier')
export class QuoteItemPriceAdjustmentSupplier extends QuoteItemPriceAdjustment {
  private buyInValue: number | null = null;
  private _showExtendedlistPrice: boolean;
  private _showMultiplier: boolean;
  private _showSupplierPriceAdjustment: boolean;

  constructor(
    isClientFacing: boolean,
    quoteManager: QuoteContainerManager,
    quoteItemPrice: QuoteItemPrice,
    quantity: number,
    forceReadonly: boolean,
    buyInCost: StockLookupViewExtra[] | undefined,
    showExtendedListPrice: boolean,
    showMultiplier: boolean,
    showSupplierPriceAdjustment: boolean
  ) {
    super(isClientFacing, quoteManager, quoteItemPrice, quantity, forceReadonly, buyInCost);
    this._showExtendedlistPrice = showExtendedListPrice;
    this._showMultiplier = showMultiplier;
    this._showSupplierPriceAdjustment = showSupplierPriceAdjustment;

    this.dataTracker.eventChange = () => this.requestUpdate();
    addDynamicMoney(
      this.dataTracker,
      'extendedListPrice',
      () => this.quoteItemPrice.supplierGrossQuantityCost,
      (_value: EventValue) => {
        console.log('extendedListPrice readonly');
      },
      () => true
    );

    // Discount/multiplier = supplierGrossQuantityCost - supplierNettQuantityCost
    // This value is to be "subtracted" from the list price and thus multiplied by -1 to show that it is subtracted
    // This binding is not used, only for display purposes.
    addDynamicMoney(
      this.dataTracker,
      'multiplier',
      () => (this.quoteItemPrice.supplierGrossQuantityCost - this.quoteItemPrice.supplierNettQuantityCost) * -1,
      (_value: EventValue) => {
        console.log('multiplier readonly');
      },
      () => true
    );

    this.dataTracker.addObjectBinding(
      () => this.quoteItemPrice,
      'quantityCost',
      'quantityCost',
      FieldType.money,
      false
    );

    this.dataTracker.addDynamic(
      'supplierPriceAdjustment',
      FieldType.money,
      () => {
        return this.quoteItemPrice.supplierPriceAdjustmentEnabled ? this.quoteItemPrice.supplierPriceAdjustment : 0;
      },
      (value: EventValue) => {
        this.quoteItemPrice.supplierPriceAdjustment = value as number;
      }
    );

    this.dataTracker.addDynamic(
      'supplierPriceAdjustmentEnabled',
      FieldType.boolean,
      () => {
        return this.quoteItemPrice.supplierPriceAdjustmentEnabled;
      },
      (value: EventValue) => {
        const oldValue = this.quoteItemPrice.supplierPriceAdjustmentEnabled;
        this.quoteItemPrice.supplierPriceAdjustmentEnabled = value as boolean;
        if (
          oldValue !== this.quoteItemPrice.supplierPriceAdjustmentEnabled &&
          !this.quoteItemPrice.supplierPriceAdjustmentEnabled
        ) {
          this.quoteItemPrice.supplierPriceAdjustment = 0;
          this.quoteItemPrice.supplierPriceAdjustmentReason = null;
        }
      }
    );

    this.dataTracker.addDynamic(
      'supplierPriceAdjustmentReason',
      FieldType.string,
      () => {
        return this.quoteItemPrice.supplierPriceAdjustmentEnabled
          ? (this.quoteItemPrice.supplierPriceAdjustmentReason ?? '')
          : this.quoteItemPrice.supplierPriceAdjustment === 0
            ? ''
            : `${this.quoteItemPrice.supplierPriceAdjustment}, ${this.quoteItemPrice.supplierPriceAdjustmentReason ?? ''}`;
      },
      (value: EventValue) => {
        this.quoteItemPrice.supplierPriceAdjustmentReason = value as string;
      }
    );

    this.dataTracker.addObjectBinding(
      () => this.quoteItemPrice,
      'isTaxableItem',
      'isTaxableItem',
      FieldType.boolean,
      false
    );
  }

  override async afterConstruction(): Promise<void> {
    if (this.buyInCost && this.buyInCost.length > 0) {
      this.buyInValue = this.buyInCost.reduce((total, item) => total + (item.cost ?? 0), 0);
      const field = this.getBuyInFieldName();

      this.dataTracker.addBinding(
        new DynamicValueBinder(
          FieldType.money,
          false,
          () => {
            return this.buyInValue;
          },
          _value => {
            //Read-Only value
          },
          () => true
        ),
        field,
        field,
        FieldType.money,
        false
      );
    }
  }

  getMultiplierLabel(): string {
    return LangQuoteItemPriceAdjustSupplierView.multiplierLabel({
      value: (
        this.quoteItemPrice.supplierNettQuantityCost /
        (this.quoteItemPrice.supplierGrossQuantityCost + (this.quoteItemPrice.supplierGrossQuantityCost == 0 ? 1 : 0))
      ).toFixed(4)
    });
  }

  protected eventKeyupSupplierCost = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      this.eventChangeSupplierCost();
    }
  };

  protected eventChangeSupplierCost = () => {
    const p = this.quoteItemPrice;
    const newPriceAdjustment = p.quantityCost - (p.supplierNettQuantityCost + (this.buyInValue ?? 0));
    this.processSupplierPriceAdjustment(newPriceAdjustment);
  };
  protected eventKeyupSupplierPriceAdjustment = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      console.debug('eventKeyupSupplierPriceAdjustment, Enter pressed');
      this.processSupplierPriceAdjustment();
    }
  };

  protected eventChangeSupplierPriceAdjustment = () => {
    console.debug('eventChangeSupplierPriceAdjustment');
    this.processSupplierPriceAdjustment();
  };

  protected bodyTemplate(): EventTemplate {
    const forms = this.forms;
    const isSupplier = this.claims.isAgent;
    const taxableItemHtml = this.quoteManager.allowNonTaxableItems
      ? forms.switch('isTaxableItem', LangQuoteItemPriceAdjustSupplierView.isTaxableItem())
      : html``;

    const clientFacingQuantity = this.isClientFacing
      ? forms.intReadonly('quantity', LangQuoteItemPriceAdjustSupplierView.quantity())
      : html``;

    const clearEvent = (_e: Event) => {
      this.quoteItemPrice.supplierPriceAdjustmentEnabled = false;
      this.quoteItemPrice.supplierPriceAdjustment = 0;
      this.quoteItemPrice.supplierPriceAdjustmentReason = null;
      this.processSupplierPriceAdjustment(0);
      this.requestUpdate();
    };
    const supplierAdjustmentRow = forms.multiEditRow({
      hidden: !isSupplier && !this._showSupplierPriceAdjustment,
      title: LangQuoteItemPriceAdjustSupplierView.priceAdjustment(),
      slotted: html`
        ${forms.switch('supplierPriceAdjustmentEnabled', undefined, {
          readonly: !isSupplier || !this._showSupplierPriceAdjustment,
          events: {
            click: this.eventChangeSupplierPriceAdjustment
          }
        })}${isSupplier
          ? forms.buttonPrimary(LangQuoteItemPriceAdjustSupplierView.removeSupplierPriceAdjustment(), {
              readonly:
                this.quoteItemPrice.supplierPriceAdjustment === 0 &&
                isEmptyOrSpace(this.quoteItemPrice.supplierPriceAdjustmentReason),
              events: { click: clearEvent }
            })
          : nothing}${forms.money('supplierPriceAdjustment', undefined, {
          readonly: !isSupplier || !this.quoteItemPrice.supplierPriceAdjustmentEnabled,
          events: {
            blur: this.eventChangeSupplierPriceAdjustment,
            keyup: this.eventKeyupSupplierPriceAdjustment
          },
          currency: this.quoteManager.quoteCurrency
        })}
      `
    });

    const supplierAdjustmentSection = html`${supplierAdjustmentRow}
    ${forms.text1('supplierPriceAdjustmentReason', LangQuoteItemPriceAdjustSupplierView.priceAdjustmentReason(), {
      maxLength: 100,

      readonly: !isSupplier || !this.quoteItemPrice.supplierPriceAdjustmentEnabled
    })} `;

    const supplierSection = this.isClientFacing
      ? undefined
      : html`<h2>${LangQuoteItemPriceAdjustSupplierView.supplier()}</h2>
          <div class="form-column">
            ${forms.intReadonly('quantity', LangQuoteItemPriceAdjustSupplierView.quantity())}
            ${this._showExtendedlistPrice || isSupplier
              ? forms.moneyReadonly('extendedListPrice', LangQuoteItemPriceAdjustSupplierView.extendedListPrice(), {
                  toolTip: LangQuoteItemPriceAdjustSupplierView.extendedListPriceTooltip(),
                  currency: this.quoteManager.quoteCurrency
                })
              : forms.textHidden('extendedListPrice', LangQuoteItemPriceAdjustSupplierView.extendedListPrice())}
            ${this._showMultiplier || isSupplier
              ? forms.moneyReadonly('multiplier', this.getMultiplierLabel(), {
                  currency: this.quoteManager.quoteCurrency
                })
              : forms.textHidden('multiplier', this.getMultiplierLabel())}
            ${supplierAdjustmentSection}
            ${this.buyInValue
              ? forms.moneyReadonly(this.getBuyInFieldName(), LangQuoteItemPriceAdjustSupplierView.buyInItems(), {
                  currency: this.quoteManager.quoteCurrency
                })
              : html``}
            ${!isSupplier
              ? forms.moneyReadonly('quantityCost', LangQuoteItemPriceAdjustSupplierView.costPrice(), {
                  currency: this.quoteManager.quoteCurrency
                })
              : forms.money('quantityCost', LangQuoteItemPriceAdjustSupplierView.costPrice(), {
                  events: {
                    blur: this.eventChangeSupplierCost,
                    keyup: this.eventKeyupSupplierCost
                  },
                  currency: this.quoteManager.quoteCurrency
                })}
          </div> `;
    return html`
      <form class="line-item-price-adjustment form-one-col">
        <div class="row">
          ${supplierSection}
          <h2>${LangQuoteItemPriceAdjustSupplierView.franchisee()}</h2>
          <div class="form-column">
            ${clientFacingQuantity} ${this.percentMarginOrMarkupTemplate(forms)} ${this.getSubtotalTemplate(forms)}
            ${this.priceAdjustmentTemplate(forms)} ${this.getCalculatedGrossSellingPriceTemplate(forms)}
            ${taxableItemHtml}
          </div>
        </div>
      </form>
    `;
  }

  private processSupplierPriceAdjustment(value?: number) {
    console.debug('processSupplierPriceAdjustment', value, this.quoteItemPrice);
    const p = this.quoteItemPrice;
    const frameItemPrice = p.sourceData as unknown as QuoteFrameItemPrice;

    //if value is undefined then the adjustment has
    // already been applied via ui editing
    // if value is assigned, its been calculated
    // after editing quanityCost
    if (value) {
      if (p.supplierPriceAdjustment === value) return;
      p.supplierPriceAdjustment = value;
    }

    if (!isEmptyObject(frameItemPrice)) applyV6PricesToQuoteItemPrice(this.quantity, frameItemPrice, p, this.buyInCost);

    p.calculatedNetSellingPrice = money(
      this.quotePrice.calculation == QuotePriceCalculation.Margin
        ? pricingStrategies.margin.getPriceWithAdjustment(
            p.quantityCost,
            p.percentMarginOrMarkup ?? this.quotePrice.percentMarginOrMarkup,
            undefined
          )
        : pricingStrategies.markup.getPriceWithAdjustment(
            p.quantityCost,
            p.percentMarginOrMarkup ?? this.quotePrice.percentMarginOrMarkup,
            undefined
          ),
      2
    );

    p.calculatedGrossSellingPrice = money(p.calculatedNetSellingPrice + p.priceAdjustment, 2);

    this.requestUpdate();
  }

  private getBuyInFieldName(): string {
    return `taxRate_rate_${this.quoteItemPrice.id}`;
  }
}
